import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../../api/cliente';
import { Boton, Tarjeta } from './basicos';
import { Dialogo } from './dialogo';
import { Cargando, Problema, Vacio, useTextoDeError } from './estados';
import { Tabla, type Columna } from './tabla';

/**
 * Una sección con su listado y su alta.
 *
 * Casi todas las pantallas de administración son lo mismo: una tabla, un botón que abre
 * un diálogo, y una invalidación al guardar. Repetir esas cuarenta líneas por pantalla no
 * las hace más claras, las hace más fáciles de desincronizar —una que se olvida de
 * invalidar, otra que no muestra el error del alta— así que el patrón vive en un solo lugar.
 *
 * Lo que **no** abstrae: qué campos tiene el formulario ni qué columnas la tabla. Eso es
 * lo propio de cada recurso y esconderlo detrás de una configuración sería peor que
 * repetirlo.
 */
export function SeccionDeRecurso<T>({
  titulo,
  descripcion,
  clave,
  ruta,
  columnas,
  claveDeFila,
  textoVacio,
  puedeCrear,
  tituloDelAlta,
  ayudaDelAlta,
  formulario,
  cuerpoDelAlta,
  alGuardar,
}: {
  readonly titulo: string;
  readonly descripcion?: string | undefined;
  /** Clave de la consulta, para poder invalidarla desde afuera si hace falta. */
  readonly clave: readonly unknown[];
  readonly ruta: string;
  readonly columnas: readonly Columna<T>[];
  readonly claveDeFila: (fila: T) => string;
  readonly textoVacio?: string | undefined;
  readonly puedeCrear: boolean;
  readonly tituloDelAlta: string;
  readonly ayudaDelAlta?: string | undefined;
  readonly formulario: ReactNode;
  readonly cuerpoDelAlta: () => unknown;
  readonly alGuardar?: (() => void) | undefined;
}): React.ReactElement {
  const { t } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();
  const [abierto, setAbierto] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const consulta = useQuery({ queryKey: clave, queryFn: () => pedir<T[]>(ruta) });

  const crear = useMutation({
    mutationFn: () => pedir<{ id: string }>(ruta, { method: 'POST', cuerpo: cuerpoDelAlta() }),
    onSuccess: async () => {
      setAbierto(false);
      setError(null);
      alGuardar?.();
      await clienteDeConsultas.invalidateQueries({ queryKey: clave });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  return (
    <>
      <Tarjeta
        titulo={titulo}
        descripcion={descripcion}
        acciones={
          puedeCrear ? (
            <Boton
              onClick={() => {
                setAbierto(true);
              }}
            >
              {t('acciones.agregar')}
            </Boton>
          ) : undefined
        }
      >
        {consulta.isPending ? <Cargando /> : null}
        {consulta.isError ? (
          <Problema
            error={consulta.error}
            reintentar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}
        {consulta.isSuccess ? (
          <Tabla
            columnas={columnas}
            filas={consulta.data}
            claveDeFila={claveDeFila}
            vacio={<Vacio descripcion={textoVacio} />}
          />
        ) : null}
      </Tarjeta>

      <Dialogo
        abierto={abierto}
        titulo={tituloDelAlta}
        descripcion={ayudaDelAlta}
        onCerrar={() => {
          setAbierto(false);
          setError(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={crear.isPending}
            onClick={() => {
              crear.mutate();
            }}
          >
            {t('acciones.guardar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          {formulario}
          {error === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>
    </>
  );
}
